
import { Inject, Injectable, Optional } from '@angular/core';
import dayjs from 'dayjs';
import { BehaviorSubject,  distinctUntilChanged,  map,  Observable, Subscription } from 'rxjs';
import { IStatsApi } from '../api/interfaces/stats-abstract.api';
import { StatsApi } from '../api/stats.api';
import { NEVER_TOKEN } from '../constants/injection-tokens';
import { limitMaxForToday } from '../helpers/date.helper';
import { transformDaysStatsFromApiToUsableStatsDays, transformTraningLogMonthToTrainingLogMonthNubmer } from '../helpers/stats.helper';
import { StatDays, StatSportType, StatYears, TrainingLogYear } from '../models/stats.model';
import { AbstractFacade } from './abstract.facade';


// Cache valid during 2 minutes
const CACHE_VALIDITY = 120000;

export interface StatsState {
  loading: boolean;
  days: {
    [key in StatSportType]: StatDays;
  };
  years: {
    [key in StatSportType]: StatYears;
  };
}

@Injectable({
  providedIn: 'root'
})
export class StatsFacade extends AbstractFacade {
  private state: StatsState;
  public readonly store: BehaviorSubject<StatsState>;
  public readonly state$: Observable<StatsState>;
  public readonly days$: Observable<{[key in StatSportType]: StatDays}>;
  public readonly years$: Observable<{[key in StatSportType]: StatYears}>;
  protected subscriptions = new Subscription();

  constructor(
    private readonly statsApi: IStatsApi,
    @Inject(NEVER_TOKEN) @Optional() private userId: number
  ) {
    super();
    this.initializeState();
    this.store = new BehaviorSubject<StatsState>(this.state);
    this.state$ = this.store.asObservable();
    this.days$ = this.state$.pipe(map(state => state.days), distinctUntilChanged());
    this.years$ = this.state$.pipe(map(state => state.years), distinctUntilChanged());
  }

  getState$(): Observable<StatsState> {
    return this.state$;
  }

  getState(): StatsState {
    return this.state;
  }

  protected setState(newState: Partial<StatsState>) {
    this.state = {...this.state, ...newState };
    this.store.next(this.state);
  }

  private initializeState() {
    this.state = {
      loading: false,
      days: {
        [StatSportType.ALL]: [],
        [StatSportType.CYCLING]: [],
        [StatSportType.RUNNING]: [],
        [StatSportType.ROWING]: [],
      },
      years: {
        [StatSportType.ALL]: [],
        [StatSportType.CYCLING]: [],
        [StatSportType.RUNNING]: [],
        [StatSportType.ROWING]: [],
      }
    };
  }

  reset() {
    this.initializeState();
    this.subscriptions?.unsubscribe();
  }

  // TODO : Have only one userId by state to not mix everything
  loadDaysBetweenDates(userId: number, startDate: Date, endDate: Date, sportType: StatSportType) {
    const startDateLimited = limitMaxForToday(startDate);
    const endDateLimited = limitMaxForToday(endDate);

    // No cache for each day
    this.subscriptions.add(
      this.statsApi.getStatsByDays(startDateLimited, endDateLimited, sportType, userId).subscribe((stats) => {
        const days = stats.data.days ?? {};
        const formatedDays = transformDaysStatsFromApiToUsableStatsDays(days, startDateLimited, endDateLimited);
        this.mergeNewStatsToOldOnes(formatedDays, endDateLimited, sportType);
      })
    );
  }

  loadAllStatsByYears(sportType: StatSportType) {
    // TODO add cache
    this.subscriptions.add(
      this.statsApi.getAllStatsByYear(sportType, this.userId).subscribe((stats) => {
        const newYears = stats.data.map<TrainingLogYear>((year) => {
          return {
            ...year,
            months: year.months.map(transformTraningLogMonthToTrainingLogMonthNubmer),
          };
        });
        this.setState({
          years: {
            ...this.state.years,
            [sportType]: newYears
          }
        });
      })
    );
  }


  private mergeNewStatsToOldOnes(newDays: StatDays, endDate: Date, sportType: StatSportType) {
    const startDayDate = dayjs(endDate);
    // * -1 because old days are indexed at the end of the table
    const dayIndexOffset = startDayDate.diff(new Date(), 'day') * (-1);

    const newDaysStats = [
      ...this.state.days[sportType]
    ];

    for (let i = 0; i < newDays.length; ++i) {
      newDaysStats[i + dayIndexOffset] = newDays[i];
    }
    this.setState({
      days: {
        ...this.state.days,
        [sportType]: newDaysStats
      }
    });
  }
}
